import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useWallet } from '@txnlab/use-wallet-react';
import { useSnackbar } from 'notistack';
import algosdk from 'algosdk';
import { getAlgodConfigFromViteEnvironment, getIndexerConfigFromViteEnvironment } from '../utils/network/getAlgoClientConfigs';

const FAUCET_APP_ID = 3461957850;
const FAUCET_ADDRESS = algosdk.getApplicationAddress(FAUCET_APP_ID);
const LOGICSIG_ADDRESS = 'UROWNIR6GS4KFOEG2T4DY6BVQG5YNDZJFUBMHNVP2QJPB54A4ZJIRYY3DI';
const CREATOR_ADDRESS = 'GET2TVGHL7H3UFFIY4VGIKYYXIVBDX6VCRZBEEW7GEHLHEGPYVLYKJRAVM';
const FAUCET_URL = `https://explorer.perawallet.app/address/${FAUCET_ADDRESS}/`;
const LOGICSIG_URL = `https://explorer.perawallet.app/address/${LOGICSIG_ADDRESS}/`;
const CREATOR_URL = `https://explorer.perawallet.app/address/${CREATOR_ADDRESS}/`;

type DonationEntry = { sender: string; amountMicro: number; round: number; txid: string; target: DonationTarget };
type DonationTarget = 'FAUCET' | 'LOGICSIG' | 'CREATOR';

const Donation: React.FC = () => {
  const { activeAddress, transactionSigner } = useWallet();
  const { enqueueSnackbar } = useSnackbar();

  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'donate' | 'top'>('donate');
  const contentRef = useRef<HTMLDivElement | null>(null);

  const [donationAmt, setDonationAmt] = useState<string>('');
  const [sending, setSending] = useState(false);
  const [donationTarget, setDonationTarget] = useState<DonationTarget>('FAUCET');

  const [donations, setDonations] = useState<DonationEntry[]>([]);
  const [loadingList, setLoadingList] = useState(false);
  const [indexerAvailable, setIndexerAvailable] = useState<boolean>(true);
  const [userBalance, setUserBalance] = useState<number | null>(null);
  const [userMBR, setUserMBR] = useState<number | null>(null);

  const algodClient = useMemo(() => {
    const cfg = getAlgodConfigFromViteEnvironment();
    return new algosdk.Algodv2(String(cfg.token), cfg.server, cfg.port);
  }, []);

  const indexerClient = useMemo(() => {
    try {
      const cfg = getIndexerConfigFromViteEnvironment();
      return new algosdk.Indexer(String(cfg.token as any), cfg.server, cfg.port);
    } catch {
      setIndexerAvailable(false);
      return null;
    }
  }, []);

  const verifyNetwork = async (): Promise<boolean> => {
    try {
      const sp = await algodClient.getTransactionParams().do();
      return (sp as any)?.genesisID?.toLowerCase?.().startsWith('mainnet');
    } catch {
      return false;
    }
  };

  const shorten = (address: string | undefined | null) => {
    const s = address != null ? String(address) : '';
    return s.length > 6 ? `${s.slice(0, 3)}...${s.slice(-3)}` : s;
  };

  const handleCopy = async (textToCopy: string) => {
    try {
      await navigator.clipboard.writeText(textToCopy);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const switchTab = (tab: 'donate' | 'top') => {
    if (tab === activeTab) return;
    setActiveTab(tab);
    if (contentRef.current) contentRef.current.scrollTop = 0;
  };

  const fetchTopDonations = async () => {
    if (!indexerClient) return;
    setLoadingList(true);
    try {
      const hasDonationNote = (t: any): boolean => {
        const noteB64 = t?.note;
        if (!noteB64) return false;
        try {
          const bytes = Uint8Array.from(Buffer.from(noteB64, 'base64'));
          const note = new TextDecoder().decode(bytes).replace(/\0+$/,'').trim();
          return note === 'Donation';
        } catch {
          return false;
        }
      };
      const toEntry = (t: any, target: DonationTarget): DonationEntry => ({
        sender: t.sender,
        amountMicro: Number(t?.paymentTransaction?.amount || 0),
        round: Number(t['confirmed-round'] || t['confirmedRound'] || 0),
        txid: String(t.id || t.txid || ''),
        target,
      });

      const receivers: { address: string; target: DonationTarget }[] = [
        { address: FAUCET_ADDRESS, target: 'FAUCET' },
        { address: LOGICSIG_ADDRESS, target: 'LOGICSIG' },
        { address: CREATOR_ADDRESS, target: 'CREATOR' },
      ];
      const allEntries: DonationEntry[] = [];
      for (const { address, target } of receivers) {
        const res = await (indexerClient as any)
          .searchForTransactions()
          .txType('pay')
          .address(address)
          .addressRole('receiver')
          .limit(100)
          .do();
        const txs = (res?.transactions || []) as any[];
        const filtered = txs.filter(hasDonationNote);
        allEntries.push(...filtered.map((t) => toEntry(t, target)));
      }
      allEntries.sort((a, b) => b.amountMicro - a.amountMicro);
      setDonations(allEntries);
    } catch (e) {
      setIndexerAvailable(false);
    }
    setLoadingList(false);
  };

  useEffect(() => {
    if (!open) return;
    if (activeTab === 'top') fetchTopDonations();
  }, [open, activeTab]);

  const fetchUserAccountStats = async () => {
    if (!activeAddress) { setUserBalance(null); setUserMBR(null); return }
    try {
      const info = await algodClient.accountInformation(activeAddress).do();
      setUserBalance(Number(info.amount || 0) / 1_000_000);
      setUserMBR(Number(info.minBalance || 0) / 1_000_000);
    } catch {
      setUserBalance(null);
      setUserMBR(null);
    }
  };

  useEffect(() => {
    if (!open || activeTab !== 'donate') return;
    fetchUserAccountStats();
  }, [open, activeTab, activeAddress]);

  useEffect(() => {
    const frame = document.getElementById('frame');
    const slot = document.getElementById('donation-ad');
    if (frame && slot) slot.appendChild(frame);
  }, []);

  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener('algofaucet:donation:open', onOpen);
    return () => window.removeEventListener('algofaucet:donation:open', onOpen);
  }, []);

  const performDonate = async (amtAlgo: number) => {
    if (!activeAddress || !transactionSigner) {
      enqueueSnackbar('Please connect your wallet first', { variant: 'error' });
      return;
    }
    if (!isFinite(amtAlgo) || amtAlgo <= 0) {
      enqueueSnackbar('Invalid amount', { variant: 'error' });
      return;
    }
    const isMainnet = await verifyNetwork();
    if (!isMainnet) {
      enqueueSnackbar('Please switch to MainNet in your wallet', { variant: 'error' });
      return;
    }
    setSending(true);
    try {
      const sp = await algodClient.getTransactionParams().do();
      const amt = Math.round(amtAlgo * 1_000_000);
      const note = new TextEncoder().encode('Donation');
      const receiverAddress =
        donationTarget === 'FAUCET'
          ? FAUCET_ADDRESS
          : donationTarget === 'LOGICSIG'
          ? LOGICSIG_ADDRESS
          : CREATOR_ADDRESS;
      const txn = algosdk.makePaymentTxnWithSuggestedParamsFromObject({
        sender: activeAddress,
        receiver: receiverAddress,
        amount: amt,
        note,
        suggestedParams: sp as any,
      } as any);
      const signed = await transactionSigner([txn], [0]);
      const res = await algodClient.sendRawTransaction(signed).do();
      await algosdk.waitForConfirmation(algodClient, res.txid, 6);
      enqueueSnackbar('Donation sent!', { variant: 'success' });
      setDonationAmt('');
      await fetchUserAccountStats();
      try { window.dispatchEvent(new Event('algofaucet:refresh-balance')); } catch {}
      if (activeTab === 'top') fetchTopDonations();
    } catch (e: any) {
      enqueueSnackbar(`Donation failed: ${e?.message || 'error'}`, { variant: 'error' });
    }
    setSending(false);
  };

  const handleSetMaxDonation = () => {
    if (userBalance == null || userMBR == null) return;
    const buffer = 0.001;
    const max = userBalance - userMBR - buffer;
    if (!isFinite(max) || max <= 0) {
      enqueueSnackbar('Not enough balance to donate', { variant: 'error' });
      return;
    }
    setDonationAmt(max.toFixed(6));
  };

  const Row: React.FC<{ label: string; address: string; linkUrl: string }> = ({ label, address, linkUrl }) => (
    <div className="donation-line">
      <span className="donation-label" onClick={() => window.open(linkUrl, '_blank', 'noopener,noreferrer')}>
        {label}
      </span>
      <span className="donation-address" onClick={() => handleCopy(address)}>
        {shorten(address)}
      </span>
    </div>
  );

  return (
    <>
    <div className="donation-container px-panel">
      <Row label="FAUCET" address={FAUCET_ADDRESS} linkUrl={FAUCET_URL} />
      <Row label="LOGICSIG" address={LOGICSIG_ADDRESS} linkUrl={LOGICSIG_URL} />
      <Row label="CREATOR" address={CREATOR_ADDRESS} linkUrl={CREATOR_URL} />

      {open && typeof window !== 'undefined' && createPortal(
        <div className="px-overlay" data-modal-root="true">
          <div className="px-modal px-modal-fixed">
            <div className="px-modal-head">
              <div className="px-tabs px-tabs-same">
                <button className={`px-btn${activeTab === 'donate' ? ' is-on' : ''}`} type="button" onClick={() => switchTab('donate')}>+</button>
                <button className={`px-btn${activeTab === 'top' ? ' is-on' : ''}`} type="button" onClick={() => switchTab('top')}>TOP</button>
              </div>
              <button className="px-btn" type="button" onClick={() => setOpen(false)}>X</button>
            </div>

            <div ref={contentRef} className="px-body hidden-scrollbar">
              {activeTab === 'donate' && (
                <div className="px-body-center">
                  <div className="px-stat">
                    <span>Donate to</span>
                    <div className="px-chip-row">
                      {(['FAUCET', 'LOGICSIG', 'CREATOR'] as DonationTarget[]).map((target) => (
                        <button
                          key={target}
                          type="button"
                          className={`px-btn${donationTarget === target ? ' is-on' : ''}`}
                          onClick={() => setDonationTarget(target)}
                        >
                          {target}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="px-stat">
                    <span>Balance</span>
                    <span>{userBalance != null ? userBalance.toFixed(6) : '—'} ALGO</span>
                  </div>
                  <div className="px-stat">
                    <span>MBR</span>
                    <span>{userMBR != null ? userMBR.toFixed(6) : '—'} ALGO</span>
                  </div>
                  <div className="px-form px-form-row">
                    <input
                      className="px-input"
                      type="number"
                      inputMode="decimal"
                      step="1"
                      min="0"
                      value={donationAmt}
                      onChange={(e) => setDonationAmt(e.target.value)}
                      placeholder="0.0"
                    />
                    <button type="button" className="px-btn" onClick={handleSetMaxDonation}>MAX</button>
                    <button
                      type="button"
                      className="px-btn"
                      disabled={sending}
                      onClick={async () => {
                        const n = Number(donationAmt);
                        if (isFinite(n) && n > 0) {
                          await performDonate(n)
                        } else {
                          enqueueSnackbar('Invalid amount', { variant: 'error' })
                        }
                      }}
                    >
                      SEND
                    </button>
                  </div>
                  <div style={{ textAlign: 'center', marginTop: 10 }}>Note: Donation</div>
                </div>
              )}

              {activeTab === 'top' && (
                <div>
                  {!indexerAvailable ? (
                    <div>Indexer unavailable.</div>
                  ) : loadingList ? (
                    <div>Loading…</div>
                  ) : donations.length === 0 ? (
                    <div style={{ textAlign: 'center' }}>No donations found</div>
                  ) : (
                    donations.map((d, idx) => (
                      <div key={d.txid + d.target} className="px-row">
                        <span>{idx + 1}.</span>
                        <span>
                          {shorten(d.sender)}{(activeAddress && d.sender === activeAddress) ? ' [You]' : ''}
                        </span>
                        <span>{d.target}</span>
                        <span>{(d.amountMicro / 1_000_000).toFixed(6)} ALGO</span>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
    <div className="px-panel donation-ad" id="donation-ad" />
    </>
  );
};

export default Donation;


